import React from 'react';

const EscrowDetailContent = ({ 
  escrow, 
  notes, 
  setNotes, 
  timeline, 
  relatedTransactions, 
  formatDate, 
  formatEscrowAmount 
}) => {
  const getFeeBreakdown = () => {
    if (!escrow) return null;

    return {
      'Escrow Amount': escrow.amount,
      'Platform Fee': escrow.platform_fee_total || 0,
      'Payment Fee': escrow.paystack_fee_total || 0,
      'Total Fees': (escrow.platform_fee_total || 0) + (escrow.paystack_fee_total || 0),
      'Buyer Share': escrow.buyer_fee_share || 0,
      'Seller Share': escrow.seller_fee_share || 0,
      'Seller Receives': escrow.seller_receives_net || escrow.amount
    };
  };

  const feeBreakdown = getFeeBreakdown();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left Column - Overview & Details */}
      <div className="lg:col-span-2 space-y-6">
        {/* Escrow Details Card */}
        <div className="bg-gray-800 rounded-xl border border-gray-700">
          <div className="p-6 border-b border-gray-700">
            <h3 className="text-lg font-semibold text-white flex items-center">
              <span className="mr-2 text-blue-400">💰</span>
              Escrow Details
            </h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Listing Title</label>
                  <p className="text-lg font-semibold text-white">{escrow?.listing_title || 'Chat Escrow'}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Escrow Type</label>
                    <div className="flex items-center space-x-2 mt-1">
                      {escrow?.order_id ? (
                        <>
                          <span className="text-purple-400">📦</span>
                          <span className="font-medium text-gray-300">Order-based</span>
                          <span className="text-sm text-gray-500">#{escrow.order_id}</span>
                        </>
                      ) : (
                        <>
                          <span className="text-indigo-400">💬</span>
                          <span className="font-medium text-gray-300">Chat-initiated</span>
                          <span className="text-sm text-gray-500">#{escrow?.conversation_id}</span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Dispute Status</label>
                    {escrow?.dispute_status ? (
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-[#ff660e]">⚠️</span>
                        <span className="font-medium text-[#ff660e]">{escrow.dispute_status}</span>
                        <span className="text-sm text-gray-500">#{escrow.dispute_id}</span>
                      </div>
                    ) : (
                      <span className="text-gray-500">No dispute</span>
                    )}
                  </div>
                </div>

                {/* Payment Reference */}
                {escrow?.payment_reference && (
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">Payment Reference</label>
                    <code className="text-sm bg-gray-900 px-3 py-1 rounded font-mono text-gray-300">
                      {escrow.payment_reference}
                    </code>
                  </div>
                )}
              </div>

              {/* Timeline Info */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Timeline</label>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Created</span>
                      <span className="text-sm font-medium text-gray-300">{formatDate(escrow?.created_at)}</span>
                    </div>
                    {escrow?.released_at && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-green-400">Released</span>
                        <span className="text-sm font-medium text-gray-300">{formatDate(escrow.released_at)}</span>
                      </div>
                    )}
                    {escrow?.refunded_at && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-red-400">Refunded</span>
                        <span className="text-sm font-medium text-gray-300">{formatDate(escrow.refunded_at)}</span>
                      </div>
                    )}
                    {escrow?.dispute_opened_at && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-[#ff660e]">Dispute Opened</span>
                        <span className="text-sm font-medium text-gray-300">{formatDate(escrow.dispute_opened_at)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Fee Breakdown Card */}
        <div className="bg-gray-800 rounded-xl border border-gray-700">
          <div className="p-6 border-b border-gray-700">
            <h3 className="text-lg font-semibold text-white flex items-center">
              <span className="mr-2 text-indigo-400">💸</span>
              Fee Breakdown
            </h3>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {feeBreakdown && Object.entries(feeBreakdown).map(([key, value]) => (
                <div key={key} className="flex justify-between items-center py-2 border-b border-gray-700 last:border-0">
                  <span className={`text-sm ${
                    ['Escrow Amount', 'Seller Receives'].includes(key) 
                      ? 'font-semibold text-gray-300' 
                      : 'text-gray-400'
                  }`}>
                    {key}
                  </span>
                  <span className={`font-medium ${
                    key === 'Seller Receives' ? 'text-green-400' :
                    key === 'Escrow Amount' ? 'text-blue-400' :
                    'text-gray-300'
                  }`}>
                    {formatEscrowAmount(value)}
                  </span>
                </div>
              ))}
            </div>
            
            {escrow?.fee_breakdown_parsed && (
              <div className="mt-6 p-4 bg-gray-900 rounded-lg">
                <h4 className="text-sm font-medium text-gray-300 mb-2">Detailed Breakdown</h4>
                <pre className="text-xs text-gray-400 overflow-x-auto">
                  {JSON.stringify(escrow.fee_breakdown_parsed, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>

        {/* Parties Information */}
        <div className="bg-gray-800 rounded-xl border border-gray-700">
          <div className="p-6 border-b border-gray-700">
            <h3 className="text-lg font-semibold text-white flex items-center">
              <span className="mr-2 text-green-400">👥</span>
              Parties Involved
            </h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Buyer Card */}
              <div className="bg-gray-900 rounded-lg p-4">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 flex items-center justify-center text-white text-lg font-bold">
                    {escrow?.buyer_first_name?.charAt(0)}{escrow?.buyer_last_name?.charAt(0)}
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">Buyer</h4>
                    <p className="text-sm text-gray-400">ID: {escrow?.buyer_id}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center text-sm text-gray-400">
                    <span className="mr-2">👤</span>
                    {escrow?.buyer_first_name} {escrow?.buyer_last_name}
                  </div>
                  {escrow?.buyer_email && (
                    <div className="flex items-center text-sm text-gray-400">
                      <span className="mr-2">✉️</span>
                      {escrow.buyer_email}
                    </div>
                  )}
                  {escrow?.buyer_phone && (
                    <div className="flex items-center text-sm text-gray-400">
                      <span className="mr-2">📞</span>
                      {escrow.buyer_phone}
                    </div>
                  )}
                </div>
              </div>

              {/* Seller Card */}
              <div className="bg-gray-900 rounded-lg p-4">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#ff660e] to-orange-600 flex items-center justify-center text-white text-lg font-bold">
                    {escrow?.seller_first_name?.charAt(0)}{escrow?.seller_last_name?.charAt(0)}
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">Seller</h4>
                    <p className="text-sm text-gray-400">ID: {escrow?.seller_id}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center text-sm text-gray-400">
                    <span className="mr-2">👤</span>
                    {escrow?.seller_first_name} {escrow?.seller_last_name}
                  </div>
                  {escrow?.seller_email && (
                    <div className="flex items-center text-sm text-gray-400">
                      <span className="mr-2">✉️</span>
                      {escrow.seller_email}
                    </div>
                  )}
                  {escrow?.seller_phone && (
                    <div className="flex items-center text-sm text-gray-400">
                      <span className="mr-2">📞</span>
                      {escrow.seller_phone}
                    </div>
                  )}
                  {escrow?.seller_store_name && (
                    <div className="flex items-center text-sm text-gray-400">
                      <span className="mr-2">🏪</span>
                      {escrow.seller_store_name}
                    </div>
                  )}
                  {escrow?.seller_location && (
                    <div className="flex items-center text-sm text-gray-400">
                      <span className="mr-2">📍</span>
                      {escrow.seller_location}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Admin Notes Card */}
        <div className="bg-gray-800 rounded-xl border border-gray-700">
          <div className="p-6 border-b border-gray-700">
            <h3 className="text-lg font-semibold text-white flex items-center">
              <span className="mr-2 text-blue-400">📝</span>
              Admin Notes
            </h3>
          </div>
          <div className="p-6">
            <div className="bg-gray-900 rounded-lg p-4 min-h-[100px]">
              {escrow?.admin_notes ? (
                <p className="text-gray-300 whitespace-pre-wrap">{escrow.admin_notes}</p>
              ) : (
                <p className="text-gray-500 italic">No admin notes yet</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Right Column - Timeline & Actions */}
      <div className="space-y-6">
        {/* Quick Actions Card */}
        <div className="bg-gray-800 rounded-xl border border-gray-700">
          <div className="p-6 border-b border-gray-700">
            <h3 className="text-lg font-semibold text-white flex items-center">
              <span className="mr-2 text-purple-400">⚡</span>
              Quick Actions
            </h3>
          </div>
          <div className="p-6 space-y-3">
            {['release', 'refund', 'notes'].map((action, index) => (
              <button
                key={index}
                onClick={() => {
                  // This would trigger action modals
                  console.log(`Action: ${action}`);
                }}
                className="w-full px-4 py-3 bg-gray-900 hover:bg-gray-700 border border-gray-700 rounded-lg text-gray-300 hover:text-white font-medium flex items-center justify-between transition-all duration-200 hover:scale-[1.02]"
              >
                <div className="flex items-center">
                  <span className="mr-2">
                    {action === 'release' ? '📈' : 
                     action === 'refund' ? '📉' : 
                     '📝'}
                  </span>
                  {action === 'release' ? 'Release Funds' :
                   action === 'refund' ? 'Refund Funds' :
                   'Add Notes'}
                </div>
                <span className="text-gray-500">→</span>
              </button>
            ))}
          </div>
        </div>

        {/* Timeline Card */}
        <div className="bg-gray-800 rounded-xl border border-gray-700">
          <div className="p-6 border-b border-gray-700">
            <h3 className="text-lg font-semibold text-white flex items-center">
              <span className="mr-2 text-blue-400">📅</span>
              Activity Timeline
            </h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {timeline.length > 0 ? (
                timeline.map((item, index) => (
                  <div key={index} className="flex space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 rounded-full bg-blue-900/30 flex items-center justify-center">
                        <span className="text-blue-400">⚡</span>
                      </div>
                    </div>
                    <div className="flex-1 pb-4 border-b border-gray-700 last:border-0 last:pb-0">
                      <p className="text-sm font-medium text-gray-300">{item.action_type}</p>
                      <p className="text-sm text-gray-400 mt-1">{item.description}</p>
                      <p className="text-xs text-gray-500 mt-1">{formatDate(item.created_at)}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">No activity recorded</p>
              )}
            </div>
          </div>
        </div>

        {/* Related Transactions */}
        {relatedTransactions.length > 0 && (
          <div className="bg-gray-800 rounded-xl border border-gray-700">
            <div className="p-6 border-b border-gray-700">
              <h3 className="text-lg font-semibold text-white flex items-center">
                <span className="mr-2 text-green-400">💳</span>
                Related Transactions
              </h3>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                {relatedTransactions.slice(0, 3).map((transaction) => (
                  <div key={transaction.transaction_id} className="p-3 bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-300">{transaction.reference}</span>
                      <span className={`text-sm font-bold ${
                        transaction.type === 'credit' ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {formatEscrowAmount(transaction.amount)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {transaction.status} • {formatDate(transaction.created_at)}
                    </div>
                  </div>
                ))}
                {relatedTransactions.length > 3 && (
                  <button className="w-full text-center text-sm text-[#ff660e] hover:text-orange-400 font-medium">
                    View all {relatedTransactions.length} transactions →
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EscrowDetailContent;